import { ptBR } from 'date-fns/locale';
import { DayPicker, type DayPickerProps } from 'react-day-picker';
import 'react-day-picker/style.css';
import { cn } from '../../../lib/utils';

export type CalendarProps = DayPickerProps;

function ChevronLeft({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ChevronRight({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale = ptBR,
  ...props
}: CalendarProps) {
  // captionLayout `dropdown*` switches the month/year label for native <select>
  // dropdowns. In react-day-picker v9 the `caption_label` class is reused INSIDE
  // each dropdown (as the visible value), so it needs a different shape in that
  // mode; the native <select> is overlaid invisibly on top of the bordered root.
  const isDropdown =
    typeof props.captionLayout === 'string' && props.captionLayout.startsWith('dropdown');
  return (
    <DayPicker
      locale={locale}
      showOutsideDays={showOutsideDays}
      className={cn('p-3 text-sm text-foreground', className)}
      classNames={{
        months: 'flex flex-col gap-4 sm:flex-row sm:gap-6',
        month: 'flex flex-col gap-3',
        month_caption: cn(
          'flex items-center justify-center pt-1 relative',
          isDropdown ? 'h-9 px-9' : 'h-7'
        ),
        caption_label: isDropdown
          ? 'flex items-center gap-1 px-2 text-sm font-medium text-foreground capitalize [&>svg]:size-3.5 [&>svg]:text-muted-foreground'
          : 'text-sm font-medium text-foreground capitalize',
        nav: 'flex items-center gap-1 absolute inset-x-0 top-0 justify-between px-1',
        button_previous: cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md',
          'border border-input bg-background text-foreground',
          'hover:bg-accent hover:text-accent-foreground transition-colors',
          'disabled:pointer-events-none disabled:opacity-40'
        ),
        button_next: cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md',
          'border border-input bg-background text-foreground',
          'hover:bg-accent hover:text-accent-foreground transition-colors',
          'disabled:pointer-events-none disabled:opacity-40'
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday:
          'w-9 text-[0.7rem] font-medium text-muted-foreground uppercase tracking-wide normal-case',
        week: 'flex w-full mt-1.5',
        day: 'h-9 w-9 p-0 text-center align-middle text-sm relative',
        day_button: cn(
          'inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground',
          'hover:bg-accent hover:text-accent-foreground transition-colors',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary'
        ),
        selected:
          '[&_button]:bg-primary [&_button]:text-primary-foreground [&_button]:hover:bg-primary [&_button]:hover:text-primary-foreground [&_button]:font-medium',
        today:
          '[&_button]:border [&_button]:border-primary [&_button]:font-semibold [&_button]:text-primary',
        outside: '[&_button]:text-muted-foreground/40 [&_button]:opacity-60',
        disabled: '[&_button]:text-muted-foreground/40 [&_button]:pointer-events-none',
        hidden: 'invisible',
        dropdowns: 'flex items-center justify-center gap-1.5',
        dropdown_root:
          'relative inline-flex items-center rounded-md border border-input bg-background shadow-2xs focus-within:ring-2 focus-within:ring-primary/40',
        // The native <select> sits invisibly on top of the bordered root; the
        // visible value is the `caption_label` span underneath it.
        dropdown: 'absolute inset-0 cursor-pointer opacity-0',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          if (orientation === 'left') return <ChevronLeft />;
          if (orientation === 'right') return <ChevronRight />;
          // `down` (and `up`) — the dropdown caption indicator.
          return <ChevronDown />;
        },
      }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';
